import React, { useState, useEffect } from 'react';
import api, { isCancelError } from '../utils/axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowRight, Bell, Loader2, Check } from 'lucide-react';
import { scouts } from '../utils/scouts';
import { toast } from '../utils/toast';

const AISearchNew = ({
  onSearchResults,
  onFiltersApplied,
  filters,
  resultCount,
  onSaveScout,
  user,
  onRequireLogin,
  onRequireLocation,
  initialQuery
}) => {
  const INITIAL_AI_PAGE_SIZE = 50;
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [showScoutForm, setShowScoutForm] = useState(false);
  const [scoutName, setScoutName] = useState('');
  const [savedScoutPopup, setSavedScoutPopup] = useState({ show: false, scoutName: '', count: 0 });
  const [isSavingScout, setIsSavingScout] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/api/ai-status', { signal: controller.signal })
      .then((res) => setAiAvailable(res.data.available))
      .catch((err) => {
        if (isCancelError(err)) return;
        setAiAvailable(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (initialQuery) {
      setQuery((prev) => (prev === initialQuery ? prev : initialQuery));
    }
  }, [initialQuery]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) {
      toast.warning('Please enter a search query', 'Search Required');
      return;
    }
    if (!user) {
      sessionStorage.setItem('pendingSearchQuery', query);
      if (onRequireLogin) {
        onRequireLogin();
      }
      // toast.info('Please sign in to run your search. We saved your query.', 'Login Required');
      return;
    }

    if (!user.location?.lat) {
      toast.warning('Please set your location in your profile before searching.', 'Location Required');
      if (onRequireLocation) {
        onRequireLocation();
      }
      return;
    }

    setIsSearching(true);
    try {
      sessionStorage.removeItem('pendingSearchQuery');
      const response = await api.post('/api/ai-search', { query, offset: 0, limit: INITIAL_AI_PAGE_SIZE });
      onSearchResults(response.data, query);
      
      if (response.data.filters && onFiltersApplied) {
        onFiltersApplied(response.data.filters);
      }
      
      const shownCount = Array.isArray(response.data.properties) ? response.data.properties.length : 0;
      const totalCount = Number.isFinite(response.data.total) ? response.data.total : shownCount;
      const message = response.data.hasMore
        ? `Showing ${shownCount} of ${totalCount} properties`
        : `Found ${totalCount} properties`;
      toast.success(message, 'Search Complete');
    } catch (error) {
      // Ignore AbortError - request was cancelled intentionally
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error performing AI search:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      toast.error(
        `Error performing AI search: ${errorMessage}. Please check:\n- Server is running\n- Try a simpler query`,
        'Search Failed',
        6000
      );
    } finally {
      setIsSearching(false);
    }
  };

  const hasActiveFilters = Object.values(filters || {}).some(v => v) || query;

  const SAVE_SCOUT_TIMEOUT_MS = 15000;

  const handleSaveScout = async (e) => {
    e?.preventDefault?.();
    if (!scoutName.trim()) {
      toast.warning('Please enter a name for your scout', 'Name Required');
      return;
    }
    const nameToSave = scoutName.trim();
    if (!onSaveScout) {
      console.warn('[Scout] onSaveScout not provided – save skipped');
      return;
    }
    setIsSavingScout(true);
    console.log('[Scout] Save clicked – calling onSaveScout', { name: nameToSave });
    const savePromise = onSaveScout({
      name: nameToSave,
      filters: filters || {},
      query: query || '',
      resultCount: resultCount || 0,
      createdAt: new Date().toISOString()
    });
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('SAVE_SCOUT_TIMEOUT')), SAVE_SCOUT_TIMEOUT_MS);
    });
    try {
      await Promise.race([savePromise, timeoutPromise]);
      setShowScoutForm(false);
      setScoutName('');
      // Show temporary popup with scout count and name
      try {
        const list = await scouts.fetchUserScouts();
        setSavedScoutPopup({ show: true, scoutName: nameToSave, count: list.length });
        setTimeout(() => setSavedScoutPopup(prev => ({ ...prev, show: false })), 4500);
      } catch (_) {
        setSavedScoutPopup({ show: true, scoutName: nameToSave, count: 0 });
        setTimeout(() => setSavedScoutPopup(prev => ({ ...prev, show: false })), 4500);
      }
    } catch (err) {
      if (err?.message === 'SAVE_SCOUT_TIMEOUT') {
        console.error('[Scout] Save timed out after', SAVE_SCOUT_TIMEOUT_MS / 1000, 's');
        toast.error('Save took too long. Check your connection and try again.', 'Save Timed Out');
      } else {
        console.error('[Scout] Save failed – form left open', err);
        // Toast already shown by parent
      }
    } finally {
      setIsSavingScout(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-6 relative">
      {/* Saved scout popup - top right */}
      <AnimatePresence>
        {savedScoutPopup.show && (
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[#9c795b]/20 shadow-lg shadow-[#17252A]/10"
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#10b981]/15 flex items-center justify-center">
              <Check className="w-4 h-4 text-[#10b981]" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#17252A] truncate" title={savedScoutPopup.scoutName}>
                {savedScoutPopup.scoutName}
              </p>
              <p className="text-xs text-gray-500">
                {savedScoutPopup.count} of 3 scout{savedScoutPopup.count !== 1 ? 's' : ''}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Status Badge */}
      {aiAvailable && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-4"
        >
          <div className="px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-[#9c795b]/20 text-xs font-semibold tracking-wide flex items-center gap-2 shadow-sm">
            <div className="w-2 h-2 rounded-full bg-[#10b981] animate-pulse" />
            <span className="text-[#17252A]">AI Search Active</span>
          </div>
        </motion.div>
      )}

      {/* Main Search Input */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full group relative"
      >
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-[#9c795b]/20 to-[#17252A]/10 rounded-2xl blur-lg opacity-30 group-focus-within:opacity-100 transition duration-700" />

        <div className="relative bg-white p-2 rounded-2xl shadow-2xl shadow-[#9c795b]/5 border border-[#9c795b]/10 flex items-center gap-3 transition-transform duration-300 group-focus-within:scale-[1.01]">
          <div className="pl-4 text-[#9c795b]">
            <Search className="w-5 h-5" />
          </div>
          
          <input
            type="text"
            placeholder="Tell us what you're looking for..."
            className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder:text-gray-400 h-14 text-lg"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            disabled={isSearching}
          />

          {/* Scout Button - Integrated */}
          {hasActiveFilters && user && (
            <AnimatePresence>
              {!showScoutForm ? (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => {
                    // Open form immediately so the user can always name their scout.
                    // Limit is enforced when they click Save (server returns 400 if at 3).
                    setShowScoutForm(true);
                    // Optionally warn if already at limit (non-blocking)
                    scouts.fetchUserScouts().then((userScouts) => {
                      if (userScouts.length >= 3) {
                        toast.warning(
                          'You have 3 scouts. Delete one in Profile to add another.',
                          'Scout limit'
                        );
                      }
                    }).catch(() => {});
                  }}
                  className="h-12 w-12 bg-gray-900 rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors group relative"
                  title="Save as Scout"
                >
                  <Bell className="w-5 h-5" />
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="flex items-center gap-2 pr-2"
                >
                  <input
                    type="text"
                    placeholder="Scout name..."
                    value={scoutName}
                    onChange={(e) => setScoutName(e.target.value)}
                    className="h-10 px-3 rounded-lg border border-[#9c795b]/20 text-sm outline-none focus:border-[#9c795b] transition-colors"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && scoutName.trim() && !isSavingScout) {
                        e.preventDefault();
                        handleSaveScout(e);
                      } else if (e.key === 'Escape') {
                        setShowScoutForm(false);
                        setScoutName('');
                      }
                    }}
                  />
                  <motion.button
                    type="button"
                    whileHover={!isSavingScout ? { scale: 1.05 } : undefined}
                    whileTap={!isSavingScout ? { scale: 0.95 } : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSaveScout(e);
                    }}
                    disabled={!scoutName.trim() || isSavingScout}
                    className="h-10 px-4 bg-[#9c795b] text-white rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[80px]"
                  >
                    {isSavingScout ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowScoutForm(false);
                      setScoutName('');
                    }}
                    className="h-10 w-10 bg-[#17252A]/5 hover:bg-[#17252A]/10 rounded-lg flex items-center justify-center transition-colors"
                  >
                    ×
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          )}

          {/* Search Button */}
          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: '#9c795b' }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSearch}
            disabled={isSearching || !query.trim()}
            className="h-12 w-12 bg-gray-900 rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSearching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ArrowRight className="w-5 h-5" />
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Quick Examples */}
      {!query && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-wrap justify-center gap-3 mt-6 opacity-60"
        >
          {['San Francisco < $5000', 'Amsterdam Student Room', 'New York Studio'].map((tag) => (
            <motion.button
              key={tag}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setQuery(tag);
              }}
              className="px-4 py-1.5 rounded-lg border border-[#17252A]/10 text-xs font-semibold hover:border-[#9c795b] hover:text-[#9c795b] transition-colors"
            >
              {tag}
            </motion.button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default AISearchNew;

